import {CONST, TetrominoType, ColRow, InputDirection, InputState, RotateType, getBlockSize} from "../const/const";
import {ObjectBase} from './objectBase';
import {Tetromino} from "./tetromino";
import {PlayFieldEffects} from "../view/playFieldEffects";
import {PlayFieldPopup} from "../view/playFieldPopup";
import {GarbageGenerator} from "../logic/garbageGenerator";
import { BoardBlock, BoardCodec } from "../net/boardCodec";


const BLOCK_SIZE = getBlockSize();
export enum EnginePhase {
    GENERATION = 'Generation',
    FALLING = 'Falling',
    LOCK = 'Lock',
    PATTERN = 'Pattern',
    ITERATE = 'Iterate',
    ANIMATE = 'Animate',
    ELIMINATE = 'Eliminate',
    COMPLETION = 'Completion',
}

type AuthoritativeActiveState = {
    type: string;
    rotate: string;
    col: number;
    row: number;
} | null;

/**
 * Play field
 */
export class PlayField extends ObjectBase {
    private inactiveTetrominos: Tetromino[] = [];
    private activeTetromino: Tetromino = null;
    private canHold: boolean;
    public container: Phaser.GameObjects.Container;
    private maskGraphics: Phaser.GameObjects.Graphics;
    private autoDropTimer: Phaser.Time.TimerEvent;
    private areSpawnTimer: Phaser.Time.TimerEvent | null = null;
    private droppedRotateType: RotateType;
    public autoDropDelay: number = 1000;
    private effects: PlayFieldEffects;
    private popupLayer: Phaser.GameObjects.Container;
    private popup: PlayFieldPopup;
    private pendingClearedRows: number[] | null = null;
    private inactiveBlocksCache: ColRow[] | null = null;
    private currentPhase: EnginePhase = EnginePhase.COMPLETION;
    private isStopped: boolean = false;

    public get activeTetrominoInstance(): Tetromino {
        return this.activeTetromino;
    }

    public get canHoldFlag(): boolean {
        return this.canHold;
    }

    public get phase(): EnginePhase {
        return this.currentPhase;
    }

    private setPhase(phase: EnginePhase) {
        if (this.currentPhase === phase) return;
        this.currentPhase = phase;
        this.emit('phaseChange', phase);
    }

    constructor(scene: Phaser.Scene, x: number, y: number, width: number, height: number) {
        super(scene);

        // Create container and set size.
        this.container = scene.add.container(x, y);
        this.container.width = width;
        this.container.height = height;

        // Create background.
        const topPadding = BLOCK_SIZE / 3;
        let background = scene.add.graphics();
        // Set background color.
        background.fillStyle(0x000000, 0.4);
        background.fillRect(0, -topPadding, width, height+topPadding);
        // Set background border
        const borderThick = 1;
        background.lineStyle(borderThick, 0xEEEEEE, 1.0);
        background.strokeRect(-borderThick, -topPadding-borderThick, width+(borderThick*2), height+topPadding+(borderThick*2));
        this.container.add(background);

        // background grid
        for (let row = 0; row < CONST.PLAY_FIELD.ROW_COUNT; ++row)
        for (let col = 0; col < CONST.PLAY_FIELD.COL_COUNT; ++col) {
            const backgroundBlock = this.scene.add.image(
                (col * BLOCK_SIZE),
                (row * BLOCK_SIZE),
                'blockSheet', 10);
            backgroundBlock.setOrigin(0);
            backgroundBlock.setAlpha(0.3);
            if (BLOCK_SIZE != CONST.SCREEN.BLOCK_IMAGE_SIZE) backgroundBlock.setScale(BLOCK_SIZE / CONST.SCREEN.BLOCK_IMAGE_SIZE);
            this.container.add(backgroundBlock);
        }

        // Popup overlay layer — added last so it renders above everything in the field
        this.popupLayer = scene.add.container(0, 0);
        this.container.add(this.popupLayer);

        this.effects = new PlayFieldEffects(scene, this.container, this.popupLayer);
        this.popup = new PlayFieldPopup(scene, this.popupLayer);

        this.maskGraphics = this.scene.make.graphics({x: x, y: y});

        //  Create a Graphics object for mask.
        this.maskGraphics.fillStyle(0xffffff);

        //  You have to begin a path for a Geometry mask to work
        // maskShape.beginPath();
        this.maskGraphics.fillRect(0, -(BLOCK_SIZE / 3), width, height + (BLOCK_SIZE / 3));
        this.container.setMask(this.maskGraphics.createGeometryMask());
    }

    setScale(scale: number) {
        this.container.setScale(scale);
        this.maskGraphics.setScale(scale);
    }

    setPosition(x: number, y: number) {
        this.container.setPosition(x, y);
        this.maskGraphics.setPosition(x, y);
    }

    /**
     * Clear all tetromino
     */
    clear() {
        this.clearSpawnTimer();
        // Destroy active tetromino.
        if (this.activeTetromino) {
            this.container.remove(this.activeTetromino.container);
            this.activeTetromino.destroy();
        }
        // Destroy all inactive tetrominos.
        this.inactiveTetrominos.forEach(tetromino => {
            this.container.remove(tetromino.container);
            tetromino.destroy();
        });

        // Clear active tetromino.
        this.activeTetromino = null;
        // Clear inactive tetrominos.
        this.inactiveTetrominos = [];
        this.inactiveBlocksCache = [];
        this.setPhase(EnginePhase.COMPLETION);
        this.isStopped = false;
    }

    private invalidateInactiveBlocksCache() {
        this.inactiveBlocksCache = null;
    }

    /**
     * Spawn new tetromino.
     * @param {TetrominoType} type - Tetromino type.
     */
    spawnTetromino(type?: TetrominoType): void {
        if (this.isStopped) return;
        if (this.activeTetromino) return;
        this.clearSpawnTimer();
        this.setPhase(EnginePhase.GENERATION);
        // Get tetrominoType from param or generate random type from queue.
        let tetrominoType = type;
        if (!tetrominoType) this.emit('generateRandomType', (genType: TetrominoType) => tetrominoType = genType);
        // Create new tetromino.
        let tetromino = new Tetromino(this.scene, tetrominoType, this.getInactiveBlocks());
        // Check spawn is success
        if (tetromino.isSpawnSuccess) { // If spawn is success.
            // Set active tetromino with new tetromino.
            this.activeTetromino = tetromino;
            this.setPhase(EnginePhase.FALLING);
            // Add tetromino ui to play field container (popup layer stays on top).
            this.container.add(this.activeTetromino.container);
            this.container.bringToTop(this.popupLayer);
            // Check is lockable
            if (this.activeTetromino.isLockable()) {
                // If created is lockable (may be spawned top of play field.)
                // Start lock timer();
                this.startLockTimer();
            } else {
                // Normal state.
                // Move down one row immediately. - Tetris guide 2009 - 3.4
                this.activeTetromino.moveDown('autoDrop');
                if (this.activeTetromino.isLockable()) {
                    this.startLockTimer();
                }
            }
            // Start drop timer;
            this.restartAutoDropTimer();
        } else { // If spawn is fail, it means Block Out GAME OVER. - Tetris Guide 2009 Chapter 10.7
            // Destroy created tetromino.
            tetromino.destroy();
            // Emit game over event
            this.emit('gameOver', 'Block Out');
        }
        // Set can hold flag true.
        // You can only 1 time hold in 1 tetromino spawn.
        this.canHold = true;
    }

    /**
     * Get inactive block positions.
     */
    getInactiveBlocks(): ColRow[] {
        if (this.inactiveBlocksCache) {
            return this.inactiveBlocksCache;
        }

        const blocks: ColRow[] = [];
        for (const tetromino of this.inactiveTetrominos) {
            const tetrominoBlocks = tetromino.getBlocks();
            for (const block of tetrominoBlocks) {
                blocks.push(block);
            }
        }

        this.inactiveBlocksCache = blocks;
        return blocks;
    }

    /**
     * On input process.
     * @param {string} input - input value (ex. Z, X, C, UP, Down...)
     * @param {InputState} state - state (ex, PRESS -> HOLD -> HOLD -> RELEASE)
     */
    onInput(input: InputDirection, state: InputState) {
        // If active tetromino is not exists, ignore input.
        if (!this.activeTetromino) return;

        // PRESS or HOLD action.
        if (state == InputState.PRESS || state == InputState.HOLD) {
            let wasLockable: boolean;
            switch (input) {
                case "left":
                    wasLockable = this.activeTetromino.isLockable();
                    this.setLockTimer(this.activeTetromino.moveLeft(), true);
                    // If exit lockable state with action, restart auto drop timer.
                    if (wasLockable && !this.activeTetromino.isLockable()) this.restartAutoDropTimer();
                    break;
                case "right":
                    wasLockable = this.activeTetromino.isLockable();
                    this.setLockTimer(this.activeTetromino.moveRight(), true);
                    // If exit lockable state with action, restart auto drop timer.
                    if (wasLockable && !this.activeTetromino.isLockable()) this.restartAutoDropTimer();
                    break;
                case "softDrop":
                    let softDropSuccess = this.activeTetromino.moveDown('softDrop');
                    this.setLockTimer(softDropSuccess, true);
                    if (softDropSuccess) this.restartAutoDropTimer();
                    break;
            }

        }

        // Only PRESS action.
        if (state == InputState.PRESS) {
            switch (input) {
                case "clockwise":
                case "anticlockwise":
                    let beforeRow = this.activeTetromino.row;
                    this.setLockTimer(this.activeTetromino.rotate(input == "clockwise"));
                    // if dropped by rotate action. reset drop timer;
                    if (beforeRow < this.activeTetromino.row) this.restartAutoDropTimer();
                    break;
                case "hardDrop":
                    const startRow = this.activeTetromino.row;
                    this.activeTetromino.hardDrop();
                    const dropDistance = this.activeTetromino.row - startRow;
                    this.droppedRotateType = this.activeTetromino.rotateType;
                    this.playHardDropEffect(this.activeTetromino, dropDistance);
                    this.lock();
                    break;
                case "hold":
                    // If canHold flag is true and active tetromino is exists, do hold.
                    if (this.canHold && this.activeTetromino) {
                        const holdType = this.activeTetromino.type;
                        // Destroy active tetromino.
                        this.container.remove(this.activeTetromino.container);
                        this.activeTetromino.destroy();
                        this.activeTetromino = null;

                        // Do hold type and get released type.
                        this.emit('hold', holdType, (releasedType: TetrominoType) => {
                            // Spawn new tetromino with un held type.
                            this.spawnTetromino(releasedType);
                        });

                        // Set can hold flag false.
                        // You can only 1 time hold in 1 tetromino spawn.
                        this.canHold = false;
                    }
                    break;
            }
        }
    }

    /**
     * Get rows that are full and need to be cleared.
     * @param {Tetromino} lockedTetromino - The tetromino that just locked.
     * @returns {number[]} Array of row indices to clear.
     */
    getClearedRows(lockedTetromino: Tetromino): number[] {
        const rowsToCheck = new Set(lockedTetromino.getBlocks().map(([, row]) => row));
        const needClearRows: number[] = [];
        const inactiveBlocks = this.getInactiveBlocks();
        const rowCounts = new Map<number, number>();

        for (const [, row] of inactiveBlocks) {
            rowCounts.set(row, (rowCounts.get(row) || 0) + 1);
        }

        for (const row of rowsToCheck) {
            const numberOfRowBlocks = rowCounts.get(row) || 0;
            if (numberOfRowBlocks >= CONST.PLAY_FIELD.COL_COUNT) needClearRows.push(row);
        }
        return needClearRows;
    }

    /**
     * Play clear animation on specific rows.
     * @param {number[]} rows - Rows to animate.
     * @param {Function} onComplete - Callback when animation finishes.
     */
    playClearAnimation(rows: number[], onComplete: () => void) {
        this.effects.playClearAnimation(this.inactiveTetrominos, rows, onComplete);
    }

    /**
     * Clear specific rows.
     * @param {number[]} rows - Rows to clear.
     */
    clearRows(rows: number[]) {
        // Sort rows top-down (asc) to ensure shifting doesn't mess up subsequent row indices
        rows.sort((a, b) => a - b);

        // call clear line method each tetromino.
        rows.forEach(row => {
            const emptyTetrominos = this.inactiveTetrominos.filter(tetromino => tetromino.clearLine(row));
            // remove empty tetromino.
            emptyTetrominos.forEach(tetromino => {
                this.container.remove(tetromino.container);
                tetromino.destroy();
                this.inactiveTetrominos.splice(this.inactiveTetrominos.indexOf(tetromino), 1);
            });
        });
        this.invalidateInactiveBlocksCache();
    }

    /**
     * Insert garbage lines from bottom.
     * @param {number} count - Garbage line count.
     */
    insertGarbage(count: number) {
        if (count <= 0) return;

        // Move all blocks up.
        this.inactiveTetrominos.forEach(tetromino => {
            tetromino.forceMoveUp(count);
        });

        // Update pending cleared rows indices
        if (this.pendingClearedRows) {
            this.pendingClearedRows = this.pendingClearedRows.map(row => row - count);
        }

        // Also move active tetromino up to prevent collision logic weirdness
        if (this.activeTetromino) {
             this.activeTetromino.row -= count;
             this.activeTetromino.container.y -= (count * BLOCK_SIZE);
        }

        // Generate garbage lines (Broken Lines per 2009 Guideline)
        // Find previous garbage hole index to avoid same column (Guideline: hole should change)
        let previousHoleIndex = -1;
        for (let i = this.inactiveTetrominos.length - 1; i >= 0; i--) {
            const t = this.inactiveTetrominos[i];
            if (t.type === TetrominoType.GARBAGE) {
                const blocks = t.getBlocks();
                const cols = new Set(blocks.map(b => b[0]));
                for (let c = 0; c < CONST.PLAY_FIELD.COL_COUNT; c++) {
                    if (!cols.has(c)) {
                        previousHoleIndex = c;
                        break;
                    }
                }
                break;
            }
        }
        
        // Use Logic Class
        const { lines } = GarbageGenerator.generate(count, previousHoleIndex);
            
        lines.forEach(blocks => {
            // Create a garbage tetromino container
            // We use Type I as a placeholder for the class constructor, but setInactiveBlocks overwrites visuals.
            // Initialize at (0, 0) to prevent offset.
            let garbageTetromino = new Tetromino(this.scene, TetrominoType.I, [], 0, 0);
            garbageTetromino.type = TetrominoType.GARBAGE; // Explicitly set type property
            garbageTetromino.setInactiveBlocks(blocks);
            
            this.inactiveTetrominos.push(garbageTetromino);
            this.container.add(garbageTetromino.container);
        });
        this.container.bringToTop(this.popupLayer);
        this.invalidateInactiveBlocksCache();

        // Update active tetromino's blocked positions map and ghost block
        if (this.activeTetromino) {
            this.activeTetromino.setBlockedPositions(this.getInactiveBlocks());
            this.activeTetromino.drawGhostBlocks();
        }

        // Check Top Out.
        // This Game Over Condition occurs when an opponent’s Line Attack forces your Blocks past the top of the 20-line Buffer Zone.
        const topOut = this.inactiveTetrominos.some(tetromino =>
            tetromino.getBlocks().some(block => block[1] < -CONST.PLAY_FIELD.ROW_COUNT)
        );
        if (topOut) {
            this.emit('gameOver', 'Top Out');
        }
    }

    /**
     * Serialize field state.
     */
    serialize(): BoardBlock[] {
        const result: BoardBlock[] = [];

        // Collect inactive blocks
        this.inactiveTetrominos.forEach(t => {
            const blocks = t.getBlocks();
            blocks.forEach(([col, row]) => {
                result.push({ col, row, type: t.type });
            });
        });

        // Collect active block
        if (this.activeTetromino) {
            const blocks = this.activeTetromino.getBlocks();
            blocks.forEach(([col, row]) => {
                result.push({ col, row, type: this.activeTetromino.type });
            });
        }

        return result;
    }


    /**
     * Serialize field state to 200-char encoded board string.
     */
    serializeEncoded(): string {
        return BoardCodec.encode(this.serialize());
    }

    /**
     * Deserialize field state (for opponent view).
     */
    deserialize(blocks: BoardBlock[] | null | undefined) {
        this.clear(); // Clears all tetrominos

        if (!Array.isArray(blocks) || blocks.length === 0) return;
        
        // Create a single dummy tetromino to display these blocks.
        // Initialize at (0, 0) to match absolute coordinates from server.
        let dummy = new Tetromino(this.scene, TetrominoType.I, [], 0, 0);
        dummy.setInactiveBlocks(blocks);
        this.inactiveTetrominos.push(dummy);
        this.container.add(dummy.container);
        this.container.bringToTop(this.popupLayer);
        this.invalidateInactiveBlocksCache();
    }

    /**
     * Deserialize encoded board state (for opponent view).
     */
    deserializeEncoded(encodedBoard: string | Uint8Array | ArrayBuffer | null) {
        this.deserialize(BoardCodec.decode(encodedBoard || ''));
    }

    applyAuthoritativeState(encodedBoardCore: string | Uint8Array | ArrayBuffer, activeState: AuthoritativeActiveState, canHold: boolean): void {
        this.clearSpawnTimer();
        this.stopLockTimer();
        this.stopAutoDropTimer();
        this.deserializeEncoded(encodedBoardCore);

        this.activeTetromino = null;
        const isValidActive = Boolean(
            activeState
            && typeof activeState.type === 'string'
            && typeof activeState.rotate === 'string'
            && Number.isFinite(activeState.col)
            && Number.isFinite(activeState.row)
            && activeState.type !== TetrominoType.GARBAGE
            && Object.values(TetrominoType).includes(activeState.type as TetrominoType)
            && Object.values(RotateType).includes(activeState.rotate as RotateType)
        );

        if (isValidActive && activeState) {
            const activeTetromino = new Tetromino(
                this.scene,
                activeState.type as TetrominoType,
                this.getInactiveBlocks(),
                activeState.col,
                activeState.row
            );

            activeTetromino.rotateType = activeState.rotate as RotateType;
            activeTetromino.col = activeState.col;
            activeTetromino.row = activeState.row;
            activeTetromino.container.x = activeState.col * BLOCK_SIZE;
            activeTetromino.container.y = activeState.row * BLOCK_SIZE;
            activeTetromino.moveBlockImages();
            activeTetromino.drawGhostBlocks();

            this.activeTetromino = activeTetromino;
            this.container.add(activeTetromino.container);
            this.container.bringToTop(this.popupLayer);
            this.setPhase(EnginePhase.FALLING);
            this.restartAutoDropTimer();
            if (this.activeTetromino.isLockable()) {
                this.startLockTimer();
            }
        } else {
            this.setPhase(EnginePhase.COMPLETION);
        }

        this.canHold = canHold;
        this.isStopped = false;
        this.invalidateInactiveBlocksCache();
    }

    /**
     * Lock tetromino.
     */
    lock() {
        // Stop lock timer.
        this.stopLockTimer();
        // If lockable active tetromino is not exist, stop function.
        if (!this.activeTetromino) return;
        if (!this.activeTetromino.isLockable()) return;
        const isImmobileAtLock = this.activeTetromino.isImmobile();
        // Stop auto drop timer.
        this.stopAutoDropTimer();
        // Inactive tetromino.
        let lockedTetromino = this.activeTetromino;
        lockedTetromino.inactive();
        this.inactiveTetrominos.push(lockedTetromino);
        this.invalidateInactiveBlocksCache();
        this.activeTetromino = null;

        // Check locked Tetromino is inside buffer zone entire.
        // Row position is smaller then zero means buffer zone.
        if (lockedTetromino.getBlocks().every(colRow => colRow[1] < 0)) {
            // Lock Out Game Over.
            // Emit game over event
            this.emit('gameOver', 'Lock Out');
            return;
        }

        this.setPhase(EnginePhase.PATTERN);

        // clear line 
        const clearedRows = this.getClearedRows(lockedTetromino);
        const clearedLineCount = clearedRows.length;

        // Proceed function
        const proceed = () => {
            this.setPhase(EnginePhase.COMPLETION);
             // Emit lock event.
            this.emit('lock',
                clearedLineCount,
                lockedTetromino.type,
                this.droppedRotateType,
                lockedTetromino.rotateType,
                lockedTetromino.lastMovement,
                lockedTetromino.lastKickDataIndex,
                lockedTetromino.dropCounter,
                lockedTetromino.getTSpinCornerOccupiedCount(),
                { isImmobile: isImmobileAtLock }
            );

            // ARE
            this.clearSpawnTimer();
            this.areSpawnTimer = this.scene.time.delayedCall(CONST.PLAY_FIELD.ARE_MS, () => {
                this.areSpawnTimer = null;
                this.spawnTetromino();
            }, [], this);
        };

        if (clearedLineCount > 0) {
            this.pendingClearedRows = clearedRows;
            this.setPhase(EnginePhase.ITERATE);
            this.setPhase(EnginePhase.ANIMATE);
            this.playClearAnimation(clearedRows, () => {
                if (this.pendingClearedRows) {
                    this.setPhase(EnginePhase.ELIMINATE);
                    this.clearRows(this.pendingClearedRows);
                    this.pendingClearedRows = null;
                }
                proceed();
            });
        } else {
            this.setPhase(EnginePhase.ITERATE);
            this.setPhase(EnginePhase.ELIMINATE);
            proceed();
        }
    }

    /**
     * Start auto drop timer.
     */
    startAutoDropTimer() {
        if (this.autoDropTimer) return;
        this.autoDropTimer = this.scene.time.addEvent({
            delay: this.autoDropDelay,
            callback: () => {
                // If active tetromino is lockable, start lock timer.
                if (this.activeTetromino &&
                    this.activeTetromino.moveDown('autoDrop') &&
                    this.activeTetromino.isLockable()
                ) {
                    // Set dropped rotate type.
                    this.droppedRotateType = this.activeTetromino.rotateType;
                    this.startLockTimer();
                }
            },
            callbackScope: this,
            loop: true
        });
    }

    /**
     * Stop auto drop timer.
     */
    stopAutoDropTimer() {
        if (!this.autoDropTimer) return;
        this.autoDropTimer.destroy();
        this.autoDropTimer = null;
    }

    /**
     * Stop and start auto drop timer.
     */
    restartAutoDropTimer() {
        this.stopAutoDropTimer();
        this.startAutoDropTimer();
    }

    /**
     * Start lock timer.
     */
    startLockTimer() {
        if (this.activeTetromino) {
            this.setPhase(EnginePhase.LOCK);
            // Lock tetromino after lock animation finished.
            this.activeTetromino.playLockAnimation(() => this.lock());
        }
    }

    /**
     * Stop lock timer.
     */
    stopLockTimer() {
        if (this.activeTetromino && this.activeTetromino.isPlayingLockAnimation()) {
            this.activeTetromino.stopLockAnimation();
        }
    }

    /**
     * Stop all timers.
     */
    stop() {
        this.isStopped = true;
        this.clearSpawnTimer();
        this.stopLockTimer();
        this.stopAutoDropTimer();
    }

    private clearSpawnTimer() {
        if (!this.areSpawnTimer) return;
        this.areSpawnTimer.destroy();
        this.areSpawnTimer = null;
    }

    /**
     * setLockTimer
     * @param {boolean} moveSuccess Was successful move.
     * @param {boolean} setDroppedRotate Set dropped rotate.
     */
    setLockTimer(moveSuccess: boolean, setDroppedRotate?: boolean): void {
        // If tetromino move success.
        if (moveSuccess) {
            // If active tetromino is lockable.
            if (this.activeTetromino.isLockable()) {
                // Set dropped rotate type.
                if (setDroppedRotate) this.droppedRotateType = this.activeTetromino.rotateType;
                
                // Restart lock timer while manipulation count is within the 15 reset limit (Extended Placement).
                if (this.activeTetromino.manipulationCount <= 15) {
                    this.stopLockTimer();
                    this.startLockTimer();
                }
            } else {
                // Stop lock timer.
                this.stopLockTimer();
            }
        }
    }

    /**
     * Play hard drop effect.
     * @param {Tetromino} tetromino - The tetromino that hard dropped.
     * @param {number} distance - The distance the tetromino dropped.
     */
    playHardDropEffect(tetromino: Tetromino, distance: number) {
        this.effects.playHardDropEffect(tetromino, distance);
    }

    /**
     * Show action/combo popup at the center of the play field.
     * @param {string|null} actionName - e.g. "Tetris", "T-Spin Double", "Back to Back Tetris"
     * @param {number} combo - Current combo count (-1 or 0 = no combo)
     */
    showPopup(actionName: string | null, combo: number) {
        this.popup.show(actionName, combo);
    }
}
